import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    compareScopeOrder,
    ScopeMemberKind,
} from "@common/helpers/domains/accounting/chartScopeOrdering";
import { RowDropPosition } from "@design-system/ViewTable/ViewTable.lib";
import { DraftAccount, DraftGroup } from "../draftState";
import { GetRowCapabilities } from "../editorCapabilities";
import { MoveInsertPosition } from "../draftMoveOperations";
import { MoveRowsArgs } from "../draftMoveRows";
import { findGroupInScope } from "../draftGroupOperations";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    DropBlockReason,
    evaluateDrop,
    isDescendantOrSelf,
    parentChainDepth,
} from "./nestedDragRules";
import { evaluateRowsDrop } from "./nestedMultiDragRules";

// Only the groups the commit minted, never an ancestor that already existed.
export interface MoveCreatedGroup {
    groupId: string;
    entityId: number;
    parentGroupId: string | null;
    name: string;
}

export interface MovedRow {
    constituentId: string;
    kind: "account" | "group";
    entityId: number;
    targetType: AccountType;
    targetParentGroupId: string | null;
    overRefId: string | null;
    position: MoveInsertPosition;
}

export type MergedDropResult =
    | { ok: true; createdGroups: MoveCreatedGroup[]; movedRows: MovedRow[] }
    | {
          ok: false;
          reason: DropBlockReason;
          createdGroups: MoveCreatedGroup[];
          movedRows: MovedRow[];
      };

export interface MergedDropDeps {
    createGroup: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        name: string;
    }) => string;
    // Constituents that share an entity, parent and neighbour move as one
    // block: repeating a single move against the same neighbour reverses them.
    moveRows: (args: MoveRowsArgs) => void;
}

export interface MergedRowsDropDeps {
    createGroup: MergedDropDeps["createGroup"];
    moveRows: (args: MoveRowsArgs) => void;
}

interface MergedDropInput {
    movingRow: NestedEditorRow;
    overRow: NestedEditorRow;
    dropPosition: RowDropPosition;
    accounts: DraftAccount[];
    groups: DraftGroup[];
    getRowCapabilities: GetRowCapabilities;
    deps: MergedDropDeps;
}

// `remainingNames` is the part of the destination name path that does not yet
// exist in that business; `existingParentGroupId` is the existing prefix's leaf.
interface EntityDestination {
    targetType: AccountType;
    // Null when nothing is minted.
    namePathType: AccountType | null;
    remainingNames: string[];
    existingParentGroupId: string | null;
    overRefId: string | null;
    position: MoveInsertPosition;
}

interface EntityDropPlan extends EntityDestination {
    entityId: number;
    constituentId: string;
    draggedKind: "account" | "group";
}

const resolveNamePath = (
    group: DraftGroup,
    groupsById: Map<string, DraftGroup>,
): string[] => {
    const names: string[] = [];
    let current: DraftGroup | undefined = group;
    const visited = new Set<string>();
    while (current && !visited.has(current.id)) {
        visited.add(current.id);
        names.unshift(current.name);
        current =
            current.parentGroupId != null
                ? groupsById.get(current.parentGroupId)
                : undefined;
    }
    return names;
};

const splitExistingPrefix = ({
    entityId,
    namePath,
    type,
    groups,
}: {
    entityId: number;
    namePath: string[];
    type: AccountType;
    groups: DraftGroup[];
}): { existingParentGroupId: string | null; remainingNames: string[] } => {
    let parentGroupId: string | null = null;
    let index = 0;
    for (; index < namePath.length; index += 1) {
        const found = findGroupInScope({
            groups,
            entityId,
            type,
            parentGroupId,
            name: namePath[index],
        });
        if (!found) {
            break;
        }
        parentGroupId = found.id;
    }
    return {
        existingParentGroupId: parentGroupId,
        remainingNames: namePath.slice(index),
    };
};

const constituentsOf = (
    row: NestedEditorRow,
): Array<DraftAccount | DraftGroup> =>
    row.kind === "account" ? row.accounts : row.groups;

const rowMemberKind = (row: NestedEditorRow): ScopeMemberKind =>
    row.kind === "group" ? "group" : "account";

// The sibling order that entity actually shows, so a collapsed row's edge and
// the block that lands beside it agree with the tree.
const orderConstituents = <T extends DraftAccount | DraftGroup>(
    constituents: T[],
    kind: ScopeMemberKind,
): T[] =>
    [...constituents].sort((left, right) =>
        compareScopeOrder(
            { id: left.id, kind, name: left.name, sortOrder: left.sortOrder },
            {
                id: right.id,
                kind,
                name: right.name,
                sortOrder: right.sortOrder,
            },
        ),
    );

const resolveEntityDestination = ({
    entityId,
    overRow,
    dropPosition,
    groups,
    groupsById,
}: {
    entityId: number;
    overRow: NestedEditorRow;
    dropPosition: RowDropPosition;
    groups: DraftGroup[];
    groupsById: Map<string, DraftGroup>;
}): EntityDestination | null => {
    const isOnGroup = dropPosition === "on" && overRow.kind === "group";
    if (isOnGroup && overRow.group) {
        const groupType = overRow.group.type;
        const namePath = resolveNamePath(overRow.group, groupsById);
        const { existingParentGroupId, remainingNames } = splitExistingPrefix({
            entityId,
            namePath,
            type: groupType,
            groups,
        });
        return {
            targetType: groupType,
            namePathType: groupType,
            remainingNames,
            existingParentGroupId,
            overRefId: null,
            position: "after",
        };
    }
    if (overRow.kind === "type" && overRow.accountType) {
        return {
            targetType: overRow.accountType,
            namePathType: null,
            remainingNames: [],
            existingParentGroupId: null,
            overRefId: null,
            position: "after",
        };
    }
    const overConstituents = orderConstituents(
        constituentsOf(overRow).filter(
            (candidate) => candidate.entityId === entityId,
        ),
        rowMemberKind(overRow),
    );
    const position: MoveInsertPosition =
        dropPosition === "before" ? "before" : "after";
    if (overConstituents.length === 0) {
        // This business has no constituent of the over-row, so there is no slot
        // to sit beside. Leaving it out beats appending it at the type root.
        return null;
    }
    // A collapsed display row is one visible slot: `before` sits before its
    // first constituent here, `after` after its last.
    const edge =
        position === "before"
            ? overConstituents[0]
            : overConstituents[overConstituents.length - 1];
    return {
        targetType: edge.type,
        namePathType: null,
        remainingNames: [],
        existingParentGroupId: edge.parentGroupId,
        overRefId: edge.id,
        position,
    };
};

const planMergedDrop = ({
    movingRow,
    overRow,
    dropPosition,
    groups,
}: Pick<
    MergedDropInput,
    "movingRow" | "overRow" | "dropPosition" | "groups"
>): EntityDropPlan[] => {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const draggedKind = movingRow.kind === "group" ? "group" : "account";
    const constituents = constituentsOf(movingRow);

    const ordered = [...constituents].sort((left, right) =>
        left.entityId !== right.entityId
            ? left.entityId - right.entityId
            : compareScopeOrder(
                  {
                      id: left.id,
                      kind: draggedKind,
                      name: left.name,
                      sortOrder: left.sortOrder,
                  },
                  {
                      id: right.id,
                      kind: draggedKind,
                      name: right.name,
                      sortOrder: right.sortOrder,
                  },
              ),
    );

    return ordered.flatMap((constituent) => {
        const destination = resolveEntityDestination({
            entityId: constituent.entityId,
            overRow,
            dropPosition,
            groups,
            groupsById,
        });
        if (destination === null) {
            return [];
        }
        return [
            {
                ...destination,
                entityId: constituent.entityId,
                constituentId: constituent.id,
                draggedKind,
            },
        ];
    });
};

const buildSingleConstituentRow = (
    movingRow: NestedEditorRow,
    plan: EntityDropPlan,
): NestedEditorRow | null => {
    if (plan.draggedKind === "account") {
        const account = movingRow.accounts.find(
            (candidate) => candidate.id === plan.constituentId,
        );
        return account
            ? {
                  ...movingRow,
                  kind: "account",
                  account,
                  accounts: [account],
                  groups: [],
              }
            : null;
    }
    const group = movingRow.groups.find(
        (candidate) => candidate.id === plan.constituentId,
    );
    return group
        ? {
              ...movingRow,
              kind: "group",
              group,
              accounts: [],
              groups: [group],
          }
        : null;
};

const evaluatePlan = ({
    plan,
    draggedRow,
    accounts,
    groups,
    getRowCapabilities,
}: {
    plan: EntityDropPlan;
    draggedRow: NestedEditorRow;
    accounts: DraftAccount[];
    groups: DraftGroup[];
    getRowCapabilities: GetRowCapabilities;
}): DropBlockReason | null => {
    const narrowedGroups = groups.filter(
        (group) => group.entityId === plan.entityId,
    );
    const narrowedAccounts = accounts.filter(
        (account) => account.entityId === plan.entityId,
    );
    const narrowedGroupsById = new Map(
        narrowedGroups.map((group) => [group.id, group]),
    );
    const isCreating = plan.remainingNames.length > 0;
    const evaluation = evaluateDrop({
        draggedRow,
        targetScope: {
            entityId: plan.entityId,
            type: plan.targetType,
            parentGroupId: plan.existingParentGroupId,
        },
        groups: narrowedGroups,
        accounts: narrowedAccounts,
        getRowCapabilities,
        destinationChainDepth:
            parentChainDepth(plan.existingParentGroupId, narrowedGroupsById) +
            plan.remainingNames.length,
        skipDuplicateName: isCreating,
    });
    return evaluation.ok ? null : evaluation.reason;
};

const evaluateMergedDrop = (
    input: Pick<
        MergedDropInput,
        | "movingRow"
        | "overRow"
        | "dropPosition"
        | "accounts"
        | "groups"
        | "getRowCapabilities"
    >,
): DropBlockReason | null => {
    for (const plan of planMergedDrop(input)) {
        const draggedRow = buildSingleConstituentRow(input.movingRow, plan);
        if (!draggedRow) {
            continue;
        }
        const reason = evaluatePlan({
            plan,
            draggedRow,
            accounts: input.accounts,
            groups: input.groups,
            getRowCapabilities: input.getRowCapabilities,
        });
        if (reason != null) {
            return reason;
        }
    }
    return null;
};

const mintRemaining = ({
    entityId,
    existingParentGroupId,
    namePathType,
    remainingNames,
    createGroup,
}: {
    entityId: number;
    existingParentGroupId: string | null;
    namePathType: AccountType | null;
    remainingNames: string[];
    createGroup: MergedDropDeps["createGroup"];
}): { targetParentGroupId: string | null; created: MoveCreatedGroup[] } => {
    const created: MoveCreatedGroup[] = [];
    let parentGroupId = existingParentGroupId;
    if (namePathType != null) {
        for (const name of remainingNames) {
            const groupId = createGroup({
                entityId,
                type: namePathType,
                parentGroupId,
                name,
            });
            created.push({ groupId, entityId, parentGroupId, name });
            parentGroupId = groupId;
        }
    }
    return { targetParentGroupId: parentGroupId, created };
};

const EMPTY_RESULT: {
    createdGroups: MoveCreatedGroup[];
    movedRows: MovedRow[];
} = {
    createdGroups: [],
    movedRows: [],
};

export const commitMergedRowDrop = (
    input: MergedDropInput,
): MergedDropResult => {
    const reason = evaluateMergedDrop(input);
    if (reason != null) {
        return { ok: false, reason, ...EMPTY_RESULT };
    }
    // One destination per entity, so a row whose constituents share a business
    // splices as a block instead of reversing against the same neighbour.
    const plansByEntity = new Map<number, EntityDropPlan[]>();
    for (const plan of planMergedDrop(input)) {
        const entityPlans = plansByEntity.get(plan.entityId) ?? [];
        entityPlans.push(plan);
        plansByEntity.set(plan.entityId, entityPlans);
    }
    const createdGroups: MoveCreatedGroup[] = [];
    const movedRows: MovedRow[] = [];
    for (const entityPlans of plansByEntity.values()) {
        const [destination] = entityPlans;
        const { targetParentGroupId, created } = mintRemaining({
            entityId: destination.entityId,
            existingParentGroupId: destination.existingParentGroupId,
            namePathType: destination.namePathType,
            remainingNames: destination.remainingNames,
            createGroup: input.deps.createGroup,
        });
        createdGroups.push(...created);
        input.deps.moveRows({
            rows: entityPlans.map((plan) => ({
                id: plan.constituentId,
                kind: plan.draggedKind,
            })),
            entityId: destination.entityId,
            targetType: destination.targetType,
            targetParentGroupId,
            overRefId: destination.overRefId,
            position: destination.position,
        });
        for (const plan of entityPlans) {
            movedRows.push({
                constituentId: plan.constituentId,
                kind: plan.draggedKind,
                entityId: plan.entityId,
                targetType: destination.targetType,
                targetParentGroupId,
                overRefId: destination.overRefId,
                position: destination.position,
            });
        }
    }
    return { ok: true, createdGroups, movedRows };
};

interface MergedRowsDropInput {
    movingRows: NestedEditorRow[];
    overRow: NestedEditorRow;
    dropPosition: RowDropPosition;
    accounts: DraftAccount[];
    groups: DraftGroup[];
    displayedEntityIds: number[];
    getRowCapabilities: GetRowCapabilities;
    deps: MergedRowsDropDeps;
}

// A union rather than a `kind` beside a widened `constituent`, so `kind` narrows
// and the account/group branches need no cast.
type EntityMember = { sourceRowId: string } & (
    | { kind: "account"; constituent: DraftAccount }
    | { kind: "group"; constituent: DraftGroup }
);

const resolveEntityMembers = ({
    movingRows,
    entityId,
    groups,
}: {
    movingRows: NestedEditorRow[];
    entityId: number;
    groups: DraftGroup[];
}): EntityMember[] => {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const members: EntityMember[] = [];
    for (const row of movingRows) {
        if (row.kind === "group") {
            for (const group of orderConstituents(
                row.groups.filter((g) => g.entityId === entityId),
                "group",
            )) {
                members.push({
                    constituent: group,
                    kind: "group",
                    sourceRowId: row.id,
                });
            }
        } else if (row.kind === "account") {
            // Two blank-code accounts in one business are one display row and
            // both move; `.find` used to leave the second behind.
            for (const account of orderConstituents(
                row.accounts.filter((a) => a.entityId === entityId),
                "account",
            )) {
                members.push({
                    constituent: account,
                    kind: "account",
                    sourceRowId: row.id,
                });
            }
        }
    }
    const selectedGroupIds = new Set(
        members
            .filter((member) => member.kind === "group")
            .map((member) => member.constituent.id),
    );
    // A selected group already carries its subtree, so drop members sitting
    // under another selected group.
    return members.filter((member) => {
        const parentGroupId = member.constituent.parentGroupId;
        if (parentGroupId == null) {
            return true;
        }
        for (const selectedGroupId of selectedGroupIds) {
            if (
                selectedGroupId !== member.constituent.id &&
                isDescendantOrSelf(parentGroupId, selectedGroupId, groupsById)
            ) {
                return false;
            }
        }
        return true;
    });
};

const memberToRow = (member: EntityMember): NestedEditorRow => {
    if (member.kind === "account") {
        const { constituent: account } = member;
        return {
            id: account.id,
            kind: "account",
            name: account.name,
            depth: 0,
            account,
            accountType: account.type,
            accounts: [account],
            groups: [],
        };
    }
    const { constituent: group } = member;
    return {
        id: group.id,
        kind: "group",
        name: group.name,
        depth: 0,
        group,
        accountType: group.type,
        accounts: [],
        groups: [group],
    };
};

interface EntityDropPlanMulti {
    entityId: number;
    members: EntityMember[];
    destination: EntityDestination;
}

// Validates every entity before mutating: sequential commitMergedRowDrop would
// reverse the block and could leave earlier entities moved after a later failure.
export const commitMergedRowsDrop = (
    input: MergedRowsDropInput,
): MergedDropResult => {
    const groupsById = new Map(input.groups.map((group) => [group.id, group]));

    const plans: EntityDropPlanMulti[] = [];
    for (const entityId of input.displayedEntityIds) {
        const members = resolveEntityMembers({
            movingRows: input.movingRows,
            entityId,
            groups: input.groups,
        });
        if (members.length === 0) {
            continue;
        }
        const destination = resolveEntityDestination({
            entityId,
            overRow: input.overRow,
            dropPosition: input.dropPosition,
            groups: input.groups,
            groupsById,
        });
        // A business without the over-row is untouched, and its rows must not
        // reach the evaluators: a phantom type-root destination there would
        // refuse the whole drop over a business the gesture never targeted.
        if (destination === null) {
            continue;
        }
        const narrowedGroups = input.groups.filter(
            (group) => group.entityId === entityId,
        );
        const narrowedAccounts = input.accounts.filter(
            (account) => account.entityId === entityId,
        );
        const narrowedGroupsById = new Map(
            narrowedGroups.map((group) => [group.id, group]),
        );
        const isCreating = destination.remainingNames.length > 0;
        const evaluation = evaluateRowsDrop({
            draggedRows: members.map(memberToRow),
            targetScope: {
                entityId,
                type: destination.targetType,
                parentGroupId: destination.existingParentGroupId,
            },
            groups: narrowedGroups,
            accounts: narrowedAccounts,
            getRowCapabilities: input.getRowCapabilities,
            destinationChainDepth:
                parentChainDepth(
                    destination.existingParentGroupId,
                    narrowedGroupsById,
                ) + destination.remainingNames.length,
            skipDuplicateName: isCreating,
        });
        if (!evaluation.ok) {
            return { ok: false, reason: evaluation.reason, ...EMPTY_RESULT };
        }
        plans.push({ entityId, members, destination });
    }

    const createdGroups: MoveCreatedGroup[] = [];
    const movedRows: MovedRow[] = [];
    for (const { entityId, members, destination } of plans) {
        const { targetParentGroupId, created } = mintRemaining({
            entityId,
            existingParentGroupId: destination.existingParentGroupId,
            namePathType: destination.namePathType,
            remainingNames: destination.remainingNames,
            createGroup: input.deps.createGroup,
        });
        createdGroups.push(...created);
        input.deps.moveRows({
            rows: members.map((member) => ({
                id: member.constituent.id,
                kind: member.kind,
            })),
            entityId,
            targetType: destination.targetType,
            targetParentGroupId,
            overRefId: destination.overRefId,
            position: destination.position,
        });
        for (const member of members) {
            movedRows.push({
                constituentId: member.constituent.id,
                kind: member.kind,
                entityId,
                targetType: destination.targetType,
                targetParentGroupId,
                overRefId: destination.overRefId,
                position: destination.position,
            });
        }
    }
    return { ok: true, createdGroups, movedRows };
};
